<template>
  <v-row>
    <v-col
      cols="12"
    >
      <v-card
        class="mb-3 pa-2"
      >
        <v-row
          justify="space-around"
          align="center"
        >
          <v-icon
            color="primary"
          >
            mdi-filter-variant
          </v-icon>
          <v-col
            cols="12"
            lg="4"
            md="4"
            sm="2"
          >
            <v-text-field
              dense
              :label="$t('common.search.activity')"
              append-icon="mdi-magnify"
              clearable
              v-model="filter.search"
              @click:append="searchActivity"
              @click:clear="loadPage"
            />
          </v-col>
          <v-chip-group
            column
          >
            <v-menu
              v-model="rangeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-chip
                  filter-icon="mdi-check"
                  @click="rangeMenu = true"
                >
                  <v-icon
                    color="primary"
                  >
                    mdi-calendar
                  </v-icon>
                </v-chip>
              </template>
              <v-date-picker
                v-model="filter.range"
                range
                @input="loadFilteredData()"
              />
            </v-menu>
            <v-chip
              filter-icon="mdi-check"
            >
              <v-icon
                color="primary"
              >
                mdi-refresh
              </v-icon>
            </v-chip>
          </v-chip-group>
        </v-row>
      </v-card>
      <v-card
        :loading="loading"
      >
        <v-card-title
          class="text-subtitle-2"
        >
          {{ $t('common.user_activity') }}
        </v-card-title>
        <v-divider />
        <v-data-table
          :headers="headers"
          :items="logs"
          :options="options"
          :server-items-length="pagination.total"
          :items-per-page="perPage"
          :footer-props="{
            itemsPerPageOptions: [15, 30, 60, 120],
            itemsPerPageText: $t('pagination.per_page'),
            pageText: '{0}-{1} of {2}'
          }"
          :loading="loading"
          show-expand
          class="elevation-1"
          @update:page="loadPage"
          @update:items-per-page="updatePerPage"
          @update:sort-by="updateSortBy"
          @update:sort-desc="updateSortDirection"
        >
          <template #[`item.id`]="{ item }">
            <v-tooltip
              top
            >
              <template
                #activator="{ on, attrs }"
              >
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.id }}
                </span>
              </template>
              Checksum: {{ item.checksum }}
            </v-tooltip>
          </template>
          <template #[`item.account_id`]="{ item }">
            <router-link
              :to="{ name: 'account', params: { accountName: item.account.name } }"
            >
              {{ item.account.company_name }}
            </router-link>
          </template>
          <template #[`item.operation`]="{ item }">
            <v-tooltip
              top
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ operationIcon(item) }}
                </v-icon>
              </template>
              {{ $t(`log.operations.${item.operation.replace(/\./g, '_')}`) }}
            </v-tooltip>
          </template>
          <template #[`item.resource_id`]="{ item }">
            <router-link
              v-if="item.resource_type === 'user' && item.resource_id === user.id"
              :to="{ name: 'profile' }"
            >
              {{ item.resource.email }}
            </router-link>
            <router-link
              v-else-if="item.resource_type === 'user'"
              :to="{ name: 'account.user', params: { accountName: item.account.name, userId: item.resource_id } }"
            >
              {{ item.resource.email }}
            </router-link>
            <router-link
              v-else-if="item.resource_type === 'account'"
              :to="{ name: 'account', params: { accountName: item.account.name } }"
            >
              {{ item.resource.company_name }}
            </router-link>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template #[`item.data-table-expand`]="{ item, isExpanded, expand }">
            <v-icon
              v-if="canBeExpanded(item)"
              :class="{
                'v-data-table__expand-icon': true,
                'v-data-table__expand-icon--active': isExpanded
              }"
              @click="expand(!isExpanded)"
            >
              $expand
            </v-icon>
          </template>
          <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              {{ item.data }}
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import formatter from 'date-fns/format';
import parseDate from 'date-fns/parse';
import url from '../../../utils/url';

export default {
  name: 'UserActivity',
  props: {
    accountName: {
      type: String,
    },
    userId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      rangeMenu: false,
      filter: {
        range: [],
        search: null,
      },
      range: [
        formatter(startOfDay(Date.now()), 'yyyy-MM-dd'),
        formatter(endOfDay(Date.now()), 'yyyy-MM-dd'),
      ],
      loading: true,
      sortBy: null,
      sortDirection: null,
      response: {},
    };
  },
  computed: {
    ...mapGetters(['user', 'formatDate']),
    page() {
      return this.$route.query.page ? +this.$route.query.page : 1;
    },
    perPage() {
      return this.$route.query.perPage ? +this.$route.query.perPage : 15;
    },
    activityUrl() {
      return url`/api/v1/accounts/${this.accountName}/users/${this.userId}/activity?page=${this.page}&perPage=${this.perPage}&orderBy=${this.sortBy || 'created_at'}&order=${this.sortDirection || 'desc'}`;
    },
    pagination() {
      const { data, ...keys } = this.response;
      return keys;
    },
    options() {
      return {
        page: this.page,
        itemsPerPage: this.perPage,
        sortBy: [this.sortBy || 'created_at'],
        sortDesc: [this.sortDirection === null || this.sortDirection === 'desc'],
        // groupBy: string[],
        // groupDesc: boolean[],
        // multiSort: boolean,
        // mustSort: boolean
      };
    },
    headers() {
      return [
        {
          text: this.$t('common.id'),
          align: 'start',
          sortable: false,
          value: 'id',
        },
        {
          text: this.$t('common.account'),
          align: 'start',
          sortable: true,
          value: 'account_id',
        },
        {
          text: this.$t('common.type'),
          align: 'start',
          sortable: true,
          value: 'operation',
        },
        {
          text: this.$t('common.resource'),
          align: 'start',
          sortable: false,
          value: 'resource_id',
        },
        {
          text: this.$t('common.message'),
          align: 'start',
          sortable: false,
          width: '30%',
          value: 'message',
        },
        {
          text: this.$t('common.date'),
          align: 'start',
          sortable: true,
          value: 'created_at',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ];
    },
    logs() {
      return this.response.data;
    },
    navigationMethod() {
      return this.$route.query.hasOwnProperty('page') ? 'replace' : 'push';
    },
  },
  async created() {
    await this.loadPage(this.page);
  },
  methods: {
    ...mapActions(['error']),
    async updatePerPage(perPage) {
      if (+this.$route.query.perPage !== perPage) {
        await this.$router[this.navigationMethod]({ query: { ...this.$route.query, perPage } });
        await this.loadPage(1);
      }
    },
    async loadFilteredData() {
      try {
        if (!this.filter.range[0] || !this.filter.range[1]) return;
        this.loading = true;
        const from = formatter(startOfDay(parseDate(
          this.filter.range[0],
          'yyyy-MM-dd',
          new Date(),
        )), 'yyyy-MM-dd HH:mm:ss');
        const to = formatter(endOfDay(parseDate(
          this.filter.range[1],
          'yyyy-MM-dd',
          new Date(),
        )), 'yyyy-MM-dd HH:mm:ss');
        const { data } = await this.http.get(
          url`/api/v1/accounts/${this.accountName}/users/${this.userId}/activity?page=${this.page}&perPage=${this.perPage}&orderBy=${this.sortBy || 'created_at'}&order=${this.sortDirection || 'desc'}&from=${from}&to=${to}`,
        );
        this.response = data;
      } catch (e) {
        this.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadPage(page) {
      try {
        this.loading = true;
        if (+this.$route.query.page !== page) {
          await this.$router[this.navigationMethod]({ query: { ...this.$route.query, page } });
        }
        if (this.filter.range[0] && this.filter.range[1]) {
          const from = formatter(startOfDay(parseDate(
            this.filter.range[0],
            'yyyy-MM-dd',
            new Date(),
          )), 'yyyy-MM-dd HH:mm:ss');
          const to = formatter(endOfDay(parseDate(
            this.filter.range[1],
            'yyyy-MM-dd',
            new Date(),
          )), 'yyyy-MM-dd HH:mm:ss');
          const { data } = await this.http.get(
            url`/api/v1/accounts/${this.accountName}/users/${this.userId}/activity?page=${this.page}&perPage=${this.perPage}&orderBy=${this.sortBy || 'created_at'}&order=${this.sortDirection || 'desc'}&from=${from}&to=${to}`,
          );
          this.response = data;
        } else {
          const { data } = await this.http.get(this.activityUrl);
          this.response = data;
        }
      } catch (e) {
        this.error(e);
      } finally {
        this.loading = false;
      }
    },
    async updateSortBy(event) {
      if (event.length) {
        const i = 0;
        this.sortBy = event[i];
      } else {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      }
      await this.loadPage(this.page);
    },
    async updateSortDirection(event) {
      if (event.length) {
        this.sortDirection = event[0] ? 'desc' : 'asc';
      } else {
        this.sortDirection = 'desc';
      }
      await this.loadPage(this.page);
    },
    operationIcon(item) {
      const translation = `log.icons.${item.operation.replace(/\./g, '_')}`;
      const icon = this.$t(translation);
      return icon !== translation ? icon : 'mdi-checkbox-blank-circle-outline';
    },
    canBeExpanded(item) {
      if (item.data) {
        if (Array.isArray(item.data)) return !!item.data.length;
        return !!Object.keys(item.data).length;
      }
      return false;
    },
    searchActivity() {

    },
  },
};
</script>
